import React, { useCallback, useEffect, useState } from "react";
import * as L from "lucide-react";
import api from "@/lib/api";
import { UpgradeButton } from "@/pages/Payment";
import { CREDITS_EVENT, fmtCredits } from "@/lib/credits";

const KIND_LABEL = {
  grant: "Monthly allowance",
  purchase: "Top-up",
  settle: "Used",
  release: "Returned",
  expire: "Expired",
  adjust: "Adjustment",
};

function whenText(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + " · " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase();
}

/**
 * CreditsScreen — what you have, what it buys, and every movement.
 * Nothing here is a surprise: the ledger shows returns as well as spends.
 */
export function CreditsScreen({ go }) {
  const [me, setMe] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/credits/me"),
        api.get("/credits/pricing"),
        api.get("/credits/ledger?limit=40"),
      ]);
      setMe(a.data);
      setPricing(b.data);
      setEntries(c.data.entries || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener(CREDITS_EVENT, onChange);
    return () => window.removeEventListener(CREDITS_EVENT, onChange);
  }, [load]);

  const pct = me?.allowance ? Math.min(100, Math.round((me.available / me.allowance) * 100)) : 0;

  return (
    <div className="p-5 space-y-5 max-w-3xl" data-testid="screen-credits">
      <div>
        <div className="text-2xl font-display">Credits</div>
        <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
          {pricing?.note || "Chat, voice conversation, reminders and memory are never metered."}
        </p>
      </div>

      {/* Balance */}
      <div className="rounded-3xl bg-[var(--rm-surface-warm)] border border-[var(--rm-border-warm)] p-5"
           data-testid="credits-balance-card">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">
              Available
            </div>
            <div className="font-display text-4xl mt-1" data-testid="credits-available">
              {loading ? "—" : fmtCredits(me?.available)}
            </div>
            <div className="text-xs text-[var(--rammy-text-muted)] mt-1">
              of {fmtCredits(me?.allowance)} on the {me?.plan || "free"} plan
              {me?.held ? ` · ${fmtCredits(me.held)} held mid-render` : ""}
            </div>
          </div>
          <button
            onClick={() => go?.("plans")}
            className="text-xs font-medium text-[var(--rm-brand)] hover:underline"
            data-testid="credits-see-plans"
          >
            See plans
          </button>
        </div>
        <div className="mt-4 h-1.5 rounded-full bg-[var(--rammy-border)] overflow-hidden">
          <div
            className="h-full rounded-full bg-[var(--rm-brand)] transition-[width] duration-500"
            style={{ width: `${pct}%` }}
            data-testid="credits-meter"
          />
        </div>
        {me?.period_end && (
          <div className="mt-2 font-mono text-[11px] text-[var(--rm-text-faint,var(--rammy-text-muted))]">
            allowance resets {whenText(me.period_end)}
          </div>
        )}
      </div>

      {/* Top-ups */}
      <div>
        <div className="text-sm font-semibold mb-2">Top up</div>
        <div className="grid gap-2 sm:grid-cols-3">
          {(pricing?.packs || []).map((p) => (
            <div key={p.id}
                 className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4"
                 data-testid={`credits-pack-${p.id}`}>
              <div className="font-display text-lg">{p.label}</div>
              <div className="text-xs text-[var(--rammy-text-muted)] mt-0.5">${p.amount} · good for 12 months</div>
              <div className="mt-3">
                <UpgradeButton
                  packageId={p.id}
                  label={`Buy for $${p.amount}`}
                  testid={`credits-buy-${p.id}`}
                  className="w-full h-9 text-xs"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-[var(--rammy-text-muted)] mt-2">
          Stripe test mode · use card 4242 4242 4242 4242.
        </div>
      </div>

      {/* What things cost */}
      <div>
        <div className="text-sm font-semibold mb-2">What things cost</div>
        <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] divide-y divide-[var(--rammy-border)]"
             data-testid="credits-pricing">
          {(pricing?.features || []).map((f) => (
            <div key={f.feature} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
              <span>{f.label}</span>
              <span className="font-mono text-xs text-[var(--rammy-text-sec)]">
                {f.per_unit
                  ? `${f.flat ? `${f.flat} + ` : ""}${f.per_unit} / ${f.unit}`
                  : `${f.flat}`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Ledger */}
      <div>
        <div className="text-sm font-semibold mb-2">Every movement</div>
        {entries.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--rammy-border)] p-6 text-center text-sm text-[var(--rammy-text-muted)]"
               data-testid="credits-ledger-empty">
            Nothing spent yet.
          </div>
        ) : (
          <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] divide-y divide-[var(--rammy-border)]"
               data-testid="credits-ledger">
            {entries.map((e) => (
              <div key={e.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`ledger-row-${e.id}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                  e.amount >= 0 ? "bg-[var(--rm-brand)]/10 text-[var(--rm-brand)]" : "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)]"
                }`}>
                  {e.amount >= 0 ? <L.Plus size={13}/> : <L.Minus size={13}/>}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{e.reason || KIND_LABEL[e.kind] || e.kind}</div>
                  <div className="font-mono text-[11px] text-[var(--rammy-text-muted)]">
                    {KIND_LABEL[e.kind] || e.kind} · {whenText(e.created_at)}
                  </div>
                </div>
                <div className={`font-mono text-sm shrink-0 ${e.amount >= 0 ? "text-[var(--rm-brand)]" : ""}`}>
                  {e.amount >= 0 ? "+" : ""}{fmtCredits(e.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreditsScreen;
